import { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import AmbientLayout from './AmbientLayout';
import { captureError } from '@/lib/error-reporter';

interface State { hasError: boolean; error: Error | null }

export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('App error boundary caught:', error, info);
    captureError(error, {
      source: 'boundary',
      context: { componentStack: info.componentStack?.slice(0, 4000) ?? '' },
    });
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <AmbientLayout>
        <div className="glass-lift p-8 text-center mt-10">
          <div className="text-5xl mb-3">🌧️</div>
          <h1 className="font-display text-2xl">A little hiccup</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Something went sideways. We've logged it and we're sorry about that.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={this.reset}
              className="rounded-2xl bg-primary text-primary-foreground px-5 py-3 text-sm font-medium"
            >
              Try again
            </button>
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
              Back to landing
            </Link>
          </div>
        </div>
      </AmbientLayout>
    );
  }
}
